import CurrenciesEnum from "../models/CurrenciesEnum";
import ICart from "../models/ICart";
import INewSupplier from "../models/INewSupplier";
import IOrder from "../models/IOrder";
import MinOrderTotalBasisEnum from "../models/MinOrderTotalBasisEnum";
import OrderStatesEnum from "../models/OrderStatesEnum";
import UserTypesEnum from "../models/UserTypesEnum";
import IUserUI from "../models/IUserUI";

/**
 * Pure helpers for the supplier minimum order total (Suppliers.has_min_order_total / min_order_total /
 * min_order_total_basis). No sockets, no contexts, so the rules stay unit-testable.
 *
 * Mirrors the API's MinimumOrderService.evaluate: the minimum is in the catalog currency, which is the
 * currency order and cart totals are stored in, so the comparison needs no conversion. Vendor-currency
 * amounts are display-only. Zero-value and credit-only orders are never blocked. The API is the authority;
 * these checks only let the UI explain and pre-empt its rejection.
 */

/** Socket Notify message the API emits below the minimum (App.tsx lower-cases it into a translation key). */
export const MIN_ORDER_TOTAL_NOT_MET_NOTIFY = "ERROR_MIN_ORDER_TOTAL_NOT_MET";

/** `errorType` of the API's 422 body below the minimum. */
export const MIN_ORDER_TOTAL_NOT_MET_ERROR_TYPE = "MinOrderTotalNotMet";

type MinimumOrderSupplier = Pick<INewSupplier, "has_min_order_total" | "min_order_total" | "min_order_total_basis">;

export interface IMinimumOrderEvaluation {
    met: boolean;
    basis: MinOrderTotalBasisEnum;
    minimum: number;
    /** The subtotal that was compared (per `basis`). */
    subtotal: number;
    shortfall: number;
}

/** The API's 422 body when an order or cart is below the minimum. */
export interface IMinOrderTotalNotMetResponse {
    success: false;
    errorType: typeof MIN_ORDER_TOTAL_NOT_MET_ERROR_TYPE;
    errorCode: string;
    message: string;
    basis: MinOrderTotalBasisEnum;
    minimum: number;
    subtotal: number;
    shortfall: number;
    currency: string;
    vendorCurrency?: string;
    convertedMinimum?: number;
    convertedSubtotal?: number;
    convertedShortfall?: number;
}

/** Formatted amounts for display: `{minimum}` and `{shortfall}` in the translation strings. */
export interface IMinimumOrderDisplayAmounts {
    minimum: string;
    shortfall: string;
}

const toAmount = (value: number | string | null | undefined): number => {
    const amount = Number(value);

    return Number.isFinite(amount) ? amount : 0;
};

const toCents = (value: number): number => Math.round(value * 100);

const round2 = (value: number): number => toCents(value) / 100;

const resolveBasis = (value: string | null | undefined): MinOrderTotalBasisEnum => {
    return value === MinOrderTotalBasisEnum.BEFORE_DISCOUNTS
        ? MinOrderTotalBasisEnum.BEFORE_DISCOUNTS
        : MinOrderTotalBasisEnum.AFTER_DISCOUNTS;
};

/** True when the supplier has the feature on with a positive minimum. */
const isEnabled = (supplier: MinimumOrderSupplier | null | undefined): boolean => {
    return !!supplier?.has_min_order_total && toAmount(supplier.min_order_total) > 0;
};

const evaluate = (input: {
    supplier: MinimumOrderSupplier;
    subTotal: number | string | null | undefined;
    orderDiscount?: number | string | null;
    promoDiscount?: number | string | null;
}): IMinimumOrderEvaluation => {
    const basis = resolveBasis(input.supplier.min_order_total_basis);
    const minimum = round2(toAmount(input.supplier.min_order_total));

    let subtotal = toAmount(input.subTotal);

    if (basis === MinOrderTotalBasisEnum.AFTER_DISCOUNTS) {
        subtotal -= toAmount(input.orderDiscount) + toAmount(input.promoDiscount);
    }

    subtotal = round2(subtotal);

    const met = !isEnabled(input.supplier) || subtotal <= 0 || toCents(subtotal) >= toCents(minimum);
    const shortfall = met ? 0 : round2(minimum - subtotal);

    return { met, basis, minimum, subtotal, shortfall };
};

/**
 * Order types the API does not hold to the minimum, for supplier users only: customers can reach these flags
 * (remake, create), so the API holds a customer's submit to the minimum whatever the order type.
 */
const isOrderExempt = (
    order: Pick<IOrder, "is_quote" | "is_template" | "is_backorder" | "is_remake" | "is_sample">,
    isSupplierUser: boolean
): boolean => {
    if (!isSupplierUser) {
        return false;
    }

    return !!(order.is_quote || order.is_template || order.is_backorder || order.is_remake || order.is_sample);
};

/**
 * Evaluation for an order about to leave PRELIMINARY, or null when the minimum does not apply (feature off,
 * not PRELIMINARY, or an exempt order type).
 */
const evaluateOrder = (
    supplier: MinimumOrderSupplier | null | undefined,
    order: IOrder | null | undefined,
    isSupplierUser: boolean
): IMinimumOrderEvaluation | null => {
    if (
        !supplier ||
        !order ||
        !isEnabled(supplier) ||
        order.order_state_id !== OrderStatesEnum.PRELIMINARY ||
        isOrderExempt(order, isSupplierUser)
    ) {
        return null;
    }

    return evaluate({
        supplier,
        subTotal: order.sub_total,
        orderDiscount: order.discount_total,
        promoDiscount: order.promo_discount_total,
    });
};

/** Evaluation for an ecommerce cart, or null when the supplier has no minimum. */
const evaluateCart = (
    supplier: MinimumOrderSupplier | null | undefined,
    cart: ICart | null | undefined
): IMinimumOrderEvaluation | null => {
    if (!supplier || !cart || !isEnabled(supplier)) {
        return null;
    }

    return evaluate({
        supplier,
        subTotal: cart.sub_total,
        orderDiscount: cart.discount_total,
        promoDiscount: cart.promo_discount_total,
    });
};

const formatAmount = (value: number, currency?: string | null): string => {
    const currencyCode = currency || "USD";

    try {
        return new Intl.NumberFormat(navigator.language || "en-US", { style: "currency", currency: currencyCode }).format(value);
    } catch {
        return `${currencyCode} ${value.toFixed(2)}`;
    }
};

const currencyNameFromId = (currencyId: number | null | undefined): string | undefined => {
    return currencyId ? CurrenciesEnum[currencyId] : undefined;
};

/**
 * Display amounts for an order. Shown in the order's (vendor) currency, converted with the exchange rate
 * frozen on the order, when the supplier has currencies on and the vendor's currency differs from the
 * catalog's (the same rule as OrderTotalsPanel's doCurrencyConversion); otherwise in the catalog currency.
 */
const getOrderDisplayAmounts = (
    evaluation: IMinimumOrderEvaluation,
    order: IOrder,
    supplier: Pick<INewSupplier, "has_currencies">
): IMinimumOrderDisplayAmounts => {
    const catalogCurrency = order.Catalog?.Currency?.name || currencyNameFromId(order.Catalog?.currency_id) || "USD";
    const orderCurrency = order.Currency?.name || currencyNameFromId(order.currency_id);

    let rate = Number(order.exchange_rate);

    if (!(rate > 0) && orderCurrency) {
        rate = Number(order.Catalog?.Currency?.rates?.[orderCurrency]);
    }

    if (supplier.has_currencies && orderCurrency && orderCurrency !== catalogCurrency && rate > 0) {
        return {
            minimum: formatAmount(round2(evaluation.minimum * rate), orderCurrency),
            shortfall: formatAmount(round2(evaluation.shortfall * rate), orderCurrency),
        };
    }

    return {
        minimum: formatAmount(evaluation.minimum, catalogCurrency),
        shortfall: formatAmount(evaluation.shortfall, catalogCurrency),
    };
};

/** Display amounts for the API's 422 body, preferring the vendor-currency amounts when it sent them. */
const getResponseDisplayAmounts = (body: IMinOrderTotalNotMetResponse): IMinimumOrderDisplayAmounts => {
    if (body.vendorCurrency && body.convertedMinimum !== undefined && body.convertedShortfall !== undefined) {
        return {
            minimum: formatAmount(body.convertedMinimum, body.vendorCurrency),
            shortfall: formatAmount(body.convertedShortfall, body.vendorCurrency),
        };
    }

    return {
        minimum: formatAmount(toAmount(body.minimum), body.currency),
        shortfall: formatAmount(toAmount(body.shortfall), body.currency),
    };
};

const isMinOrderTotalNotMetResponse = (body: unknown): body is IMinOrderTotalNotMetResponse => {
    return !!body && typeof body === "object" && (body as { errorType?: unknown }).errorType === MIN_ORDER_TOTAL_NOT_MET_ERROR_TYPE;
};

const isMinOrderTotalNotifyMessage = (message: string | null | undefined): boolean => {
    return String(message || "").toUpperCase() === MIN_ORDER_TOTAL_NOT_MET_NOTIFY;
};

/**
 * Supplier admins may submit below the minimum order total (after a confirm); other staff and customers are
 * blocked and an admin finishes the order. Mirrors the API's OrderService.canOverrideMinOrderTotal.
 */
const canOverride = (user: Pick<IUserUI, "isSupplierUser" | "userType"> | null | undefined): boolean => {
    return !!user?.isSupplierUser && user.userType?.id === UserTypesEnum.ADMIN;
};

const MinimumOrderService = {
    resolveBasis,
    canOverride,
    isEnabled,
    evaluate,
    isOrderExempt,
    evaluateOrder,
    evaluateCart,
    formatAmount,
    getOrderDisplayAmounts,
    getResponseDisplayAmounts,
    isMinOrderTotalNotMetResponse,
    isMinOrderTotalNotifyMessage,
};

export default MinimumOrderService;
